/**
 * Fractured-glass geometry.
 *
 * The first version of the `shatter` effect drew its cracks with three
 * `repeating-conic-gradient` fans. Rendered, it read as a SUNBURST: every line
 * was perfectly straight, every line reached both the impact point and the
 * frame edge, and the spacing was even. Those three things are exactly what a
 * break is not. A conic gradient cannot produce anything else - it is regular
 * by definition - so the geometry is generated here instead, the same move the
 * `strike` effect made for lightning.
 *
 * Three properties do the work:
 *   1. Radial cracks run OUTWARD from the impact and each stops at its own
 *      radius. A pane does not fail evenly in every direction.
 *   2. Each crack is roughened by midpoint displacement, so it stays slightly
 *      crooked at every scale rather than being a ruled line.
 *   3. The circumferential cracks are CHORDS between neighbouring radials, not
 *      circles, and some pairs are skipped. A closed ring reads as a target.
 *
 * Everything is DETERMINISTIC - a seeded LCG, never `Math.random`. The paths
 * end up inside a CSS string rendered on the server and again in the browser,
 * so a different result per call would be a hydration mismatch and an unstable
 * snapshot. Same reasoning as `lightningPath.ts`.
 *
 * React-free and dependency-free. See `crackPath.test.ts`.
 */

interface Pt {
  x: number;
  y: number;
}

/** One drawn crack. `w` is RELATIVE (1 = a primary radial at full weight), so
 *  the same geometry can be stamped thin for the fracture line and wide for its
 *  glow, perfectly aligned because it IS one geometry. */
export interface Crack {
  d: string;
  w: number;
}

export const CRACK_BOX = 200;
/** The impact, off-centre on purpose: dead centre reads as a decoration. */
export const CRACK_ORIGIN: Pt = { x: 88, y: 84 };

/** Linear congruential generator - the Numerical Recipes pair, as in
 *  `lightningPath.ts`. Small, seeded, reproducible. */
const lcg = (seed: number): (() => number) => {
  let s = (seed >>> 0) || 1;
  return () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296);
};

const round1 = (n: number): number => Math.round(n * 10) / 10;
const toPath = (pts: Pt[]): string => "M" + pts.map((p) => `${round1(p.x)} ${round1(p.y)}`).join("L");

/** How far outside the box a point may land. The SVG clips at its viewBox and
 *  a crack cut off square is the one thing a real crack never looks like, so
 *  the roughening is kept inside a small margin. */
const PAD = 4;
const clamp = (n: number): number => Math.min(CRACK_BOX + PAD, Math.max(-PAD, n));

/**
 * One pass of midpoint displacement. The offset is a fraction of the SEGMENT's
 * own length, which is what keeps the roughness self similar - a fixed offset
 * would smooth out as the segments shorten and the crack would end up straight
 * again at small scale, which is the whole problem being solved.
 */
function displaceOnce(pts: Pt[], rand: () => number, wobble: number, minLen: number): Pt[] {
  const out: Pt[] = [pts[0]!];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1]!;
    const b = pts[i]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy);
    if (len > minLen) {
      const off = (rand() * 2 - 1) * wobble * len;
      out.push({ x: clamp((a.x + b.x) / 2 - (dy / len) * off), y: clamp((a.y + b.y) / 2 + (dx / len) * off) });
    }
    out.push(b);
  }
  return out;
}

/** Distance from the impact to the edge of the box along `ang`.
 *
 *  Radial length has to be a fraction of THIS, not of the box width. Picking a
 *  length off the box makes a crack aimed at the near edge overshoot it, and an
 *  overshooting crack is cut square by the viewBox - the one shape a fracture
 *  never has. It also fills a rectangle properly: the cracks pointing at a far
 *  corner get to run further, which is what a break does. */
function edgeDistance(ang: number): number {
  const cx = Math.cos(ang);
  const cy = Math.sin(ang);
  const tx = cx > 0 ? (CRACK_BOX - CRACK_ORIGIN.x) / cx : cx < 0 ? -CRACK_ORIGIN.x / cx : Infinity;
  const ty = cy > 0 ? (CRACK_BOX - CRACK_ORIGIN.y) / cy : cy < 0 ? -CRACK_ORIGIN.y / cy : Infinity;
  return Math.min(tx, ty);
}

const roughen = (pts: Pt[], rand: () => number, wobble: number, passes: number, minLen: number): Pt[] => {
  let cur = pts;
  for (let i = 0; i < passes; i++) cur = displaceOnce(cur, rand, wobble, minLen);
  return cur;
};

/** A point at distance `r` along a radial that has been roughened: walk the
 *  polyline until the straight-line distance from the origin passes `r`. Using
 *  the ROUGHENED point is what makes a chord land ON its two neighbours rather
 *  than near them. */
function atRadius(pts: Pt[], r: number): Pt {
  for (const p of pts) {
    if (Math.hypot(p.x - CRACK_ORIGIN.x, p.y - CRACK_ORIGIN.y) >= r) return p;
  }
  return pts[pts.length - 1]!;
}

/**
 * The complete fracture: radials of uneven length, a few of them branching,
 * and broken chord rings between them. Returned widest first.
 */
export function crackWeb(seed: number, spokes = 15): Crack[] {
  const rand = lcg(seed);
  const cracks: Crack[] = [];
  const radials: Pt[][] = [];
  const reach: number[] = [];

  // Radials. The angles are stepped and then jittered by up to a third of a
  // step: an even fan is the sunburst this replaces, and fully random angles
  // leave gaping empty wedges.
  for (let i = 0; i < spokes; i++) {
    const step = (Math.PI * 2) / spokes;
    const ang = i * step + (rand() - 0.5) * step * 0.66;
    const r = edgeDistance(ang) * (0.38 + rand() * 0.6);
    const tip = { x: CRACK_ORIGIN.x + Math.cos(ang) * r, y: CRACK_ORIGIN.y + Math.sin(ang) * r };
    const line = roughen([{ ...CRACK_ORIGIN }, tip], rand, 0.055, 5, 6);
    radials.push(line);
    reach.push(r);
    cracks.push({ d: toPath(line), w: 0.6 + rand() * 0.4 });

    // Roughly a third of the radials throw a secondary crack. Branching is how
    // a break spends the energy it has left; without it the fan looks drawn.
    if (rand() < 0.34) {
      const j = Math.round(line.length * (0.42 + rand() * 0.3));
      const c = line[Math.min(j, line.length - 1)]!;
      const ang2 = ang + (rand() < 0.5 ? -1 : 1) * (0.3 + rand() * 0.45);
      const r2 = r * (0.24 + rand() * 0.36);
      const tip2 = { x: clamp(c.x + Math.cos(ang2) * r2), y: clamp(c.y + Math.sin(ang2) * r2) };
      cracks.push({ d: toPath(roughen([c, tip2], rand, 0.07, 4, 5)), w: 0.34 + rand() * 0.2 });
    }
  }

  // Chords. Each ring hops from one radial to the next at a shared radius and
  // skips a pair now and then, so it comes out as a run of short segments
  // rather than a circle. A chord is only drawn when BOTH radials actually
  // reach that far - joining to a crack that stopped short would draw a line
  // hanging in clear glass.
  for (const ring of [0.13, 0.23, 0.36, 0.5]) {
    for (let i = 0; i < spokes; i++) {
      const k = (i + 1) % spokes;
      const r = CRACK_BOX * ring;
      if (reach[i]! < r || reach[k]! < r) continue;
      if (rand() < 0.32) continue;
      const a = atRadius(radials[i]!, r);
      const b = atRadius(radials[k]!, r);
      cracks.push({ d: toPath(roughen([a, b], rand, 0.05, 3, 5)), w: 0.22 + rand() * 0.22 });
    }
  }

  return cracks;
}

/**
 * The fracture as an SVG data URI, ready for `mask-image`. A data URI cannot
 * read a CSS variable, so this paints WHITE and is used as a mask over a
 * coloured layer - the same arrangement as the honeycomb, circuit and
 * lightning effects.
 */
export function crackMaskUrl(cracks: Crack[], base: number): string {
  const paths = cracks.map((c) => `%3Cpath d='${c.d}' stroke-width='${round1(Math.max(0.25, c.w * base))}'/%3E`).join("");
  return (
    `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='${CRACK_BOX}' height='${CRACK_BOX}' ` +
    `viewBox='0 0 ${CRACK_BOX} ${CRACK_BOX}'%3E%3Cg fill='none' stroke='%23ffffff' stroke-linecap='round' ` +
    `stroke-linejoin='round'%3E${paths}%3C/g%3E%3C/svg%3E")`
  );
}
